import { useMemo } from 'react';
import { gql } from '@apollo/client';
import { orderBy } from 'lodash';
import {
  CAPEX_BY_CROP_METRICS,
  CAPEX_METRICS,
  getCapexGeneticStockMetrics,
} from '../constants/capexMetrics';
import { getCropNames } from '../utils/farmData';
import { useDataTransforms, useFarmName } from '../utils/hooks';
import useFarmOrSectionGroupData from '../hooks/useFarmOrSectionGroupData';
import ItemsTable from '../components/tables/ItemsTable';
import { metricsQueryString } from '../utils/utils';
import MetricsSummary from '../components/economics/MetricsSummary';
import CAPEX_SUMMARY_METRICS from '../constants/capexSummaryMetrics';
import chartTypes from '../constants/chartTypes';
import { useFilterContext } from '../contexts/FilterContext';
import { addItemColors } from '../utils/colors';
import {
  ShrinkContent,
  ShrinkContentColumn,
} from '../components/ShrinkContent';
import MetricsHeader from '../components/layout/MetricsHeader';
import SummarySection from '../components/layout/SummarySection';
import DetailsSection from '../components/layout/DetailsSection';
import Content from '../components/layout/Content';
import { CropDisplayFragment } from '../queries';
import { useSubsidySwitchContext } from '../contexts/SubsidySwitchContext';
import { isCropFilterValid } from '../utils/filters';
import { getFilterCropAggregate } from '../utils/transforms';

const QUERY_NAME = 'CapexEconQuery';
const FRAGMENT_NAME = 'CapexEconFragment';

const CapexFragment = gql`
  ${CropDisplayFragment}
  fragment ${FRAGMENT_NAME} on SectionsAggregate {
    capexes {
      id
      ${metricsQueryString(CAPEX_METRICS)}
    }
    cropAggregates(applySubsidy: $applySubsidy) {
      crop {
        ...CropDisplayFields
        unitCost(applyBuffer: $applyBuffer, applyMargin: $applyMargin, applySubsidy: $applySubsidy)
        unitCostBeforeSubsidy: unitCost(applyBuffer: $applyBuffer, applyMargin: $applyMargin, applySubsidy: false)
      }
      ecologicalSummary {
        treeCount
        replantedTreeCount
      }
      economicSummary {
        totalGeneticsCost
      }
      proratedCapexes {
        capex {
          id
          ${metricsQueryString(CAPEX_METRICS)}
        }
        proratedCost
      }
    }
  }
`;

const getSummaryData = (cropFilter) => ({ cropAggregates, capexes }) => {
  const activeCropAggregate = getFilterCropAggregate(
    { cropAggregates },
    cropFilter,
  );
  const siteInfraData = activeCropAggregate?.proratedCapexes || capexes;
  const geneticStockData = activeCropAggregate
    ? [activeCropAggregate]
    : cropAggregates;
  // Display the prorated cost if we're filtering by crop
  const siteInfraDisplayCost = activeCropAggregate
    ? 'proratedCost'
    : 'totalCost';
  return {
    siteInfrastructure: addItemColors(
      orderBy(
        siteInfraData.map(({ capex = {}, ...item }) => ({
          ...item,
          ...capex,
          displayCost: item[siteInfraDisplayCost],
        })),
        'displayCost',
        'desc',
      ),
    ),
    geneticStock: addItemColors(
      orderBy(
        geneticStockData.map(
          ({ crop, ecologicalSummary, economicSummary }) => ({
            ...crop,
            ...ecologicalSummary,
            ...economicSummary,
            displayCost: economicSummary.totalGeneticsCost,
          }),
        ),
        'displayCost',
        'desc',
      ),
    ),
  };
};

const arrayTransform = (capexData) =>
  capexData.map(({ displayCost }) => displayCost);

const CapexView = () => {
  const { crop: cropFilter } = useFilterContext().state;
  const { shouldShowSubsidies } = useSubsidySwitchContext();
  const farmName = useFarmName();

  const { msg, data } = useFarmOrSectionGroupData(
    CapexFragment,
    QUERY_NAME,
    FRAGMENT_NAME,
    [
      {
        name: 'applyBuffer',
        typeString: 'Boolean',
        value: true,
      },
      {
        name: 'applyMargin',
        typeString: 'Boolean',
        value: true,
      },
      {
        name: 'applySubsidy',
        typeString: 'Boolean',
        value: shouldShowSubsidies,
      },
    ],
  );

  const cropFilterValid = isCropFilterValid(data, cropFilter);

  const getActiveCapexes = useMemo(() => getSummaryData(cropFilter), [
    cropFilter,
  ]);

  const [crops = [], capexSummaryData = {}] = useDataTransforms(data, [
    getCropNames,
    getActiveCapexes,
  ]);

  const getCsvFilename = (title) =>
    [
      title,
      cropFilterValid ? cropFilter : 'All crops',
      ...(shouldShowSubsidies ? ['subsidies applied'] : []),
      farmName,
    ].join(' - ');

  return (
    <>
      <MetricsHeader showCropFilter crops={crops}>
        Capital
      </MetricsHeader>
      <Content>
        <SummarySection>
          <MetricsSummary
            metrics={CAPEX_SUMMARY_METRICS}
            data={capexSummaryData}
            chartType={chartTypes.ITEMIZED}
            msg={msg}
            arrayTransform={arrayTransform}
          />
        </SummarySection>
        <DetailsSection>
          <ShrinkContent className="d-flex">
            <ShrinkContentColumn className="mr-4">
              <ItemsTable
                data={capexSummaryData.siteInfrastructure}
                msg={msg}
                cols={cropFilterValid ? CAPEX_BY_CROP_METRICS : CAPEX_METRICS}
                tableHeader={<h3>Site Infrastructure Estimate</h3>}
                csvFileName={getCsvFilename('Site infrastructure estimate')}
              />
            </ShrinkContentColumn>
            <ShrinkContentColumn className="ml-4">
              <ItemsTable
                data={capexSummaryData.geneticStock}
                msg={msg}
                cols={getCapexGeneticStockMetrics(shouldShowSubsidies)}
                tableHeader={<h3>Genetic Stock</h3>}
                csvFileName={getCsvFilename('Genetic stock')}
              />
            </ShrinkContentColumn>
          </ShrinkContent>
        </DetailsSection>
      </Content>
    </>
  );
};

export default CapexView;
